import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useSearchParams } from "react-router"
import { Plus } from "lucide-react"
import { compareAsc } from "date-fns"
import { useTranslation } from "react-i18next"

import {
  FetchStatus,
  OrganizationLimitUsageItem,
  transformOrganizationLimitUsage,
  useAddWorkspaceMembersApi,
  useGetOrganizationLimitUsageApi,
  useGetWorkspaceUsersApi,
  workspaceResponseToWorkspace,
  workspaceUsersResponseToMembers,
} from "@/hooks/api"
import {
  useGetWorkspacesApi,
  workspacesResponseToWorkspaces,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import { Button, Badge, Skeleton } from "@f2e/ui/components/base"
import { EmptyDataPanel } from "@/components/System"
import { PageProvider, SearchInput } from "@/components"
import { DEFAULT_PAGE_SIZE } from "@/constants"
import { Member, NoOwner, User, Workspace, WorkspaceRole } from "@/types"
import { useStore } from "@/store"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import {
  WorkspaceCard,
  WorkspaceDialog,
  WorkspaceMembersDialog,
} from "./components"

enum GroupFilterType {
  All = "all",
  Owned = "owned",
  Joined = "joined",
  NoOwner = "no-owner",
}

const areMembersEqual = (left: Member[], right: Member[]) => {
  if (left.length !== right.length) return false

  return left.every((member, index) => {
    const target = right[index]
    return (
      member.id === target.id &&
      member.role === target.role &&
      member.email === target.email &&
      member.name === target.name
    )
  })
}

interface WorkspaceUsageStripProps {
  usage?: OrganizationLimitUsageItem | null
}

const formatCount = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const WorkspaceUsageStrip = ({ usage }: WorkspaceUsageStripProps) => {
  const { t } = useTranslation()
  const used = usage?.used ?? 0
  const limit = usage?.limit ?? null
  const atLimit = Boolean(usage?.atLimit)
  const percent =
    limit == null ? 0 : limit <= 0 ? 100 : Math.min(100, (used / limit) * 100)
  const barColor =
    atLimit || percent >= 100
      ? "bg-red-500"
      : percent >= 80
        ? "bg-yellow-500"
        : "bg-green-500"
  const textColor =
    atLimit || percent >= 100
      ? "text-red-700 dark:text-red-400"
      : percent >= 80
        ? "text-amber-700 dark:text-amber-400"
        : "text-slate-600 dark:text-slate-300"
  const limitLabel =
    limit == null ? t("workspace.list.usage.unlimited") : formatCount(limit)
  const remaining = limit == null ? null : Math.max(0, limit - used)

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/40">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
            {t("workspace.list.usage.title")}
          </div>
          <div className={`text-xs ${textColor}`}>
            {atLimit
              ? t("workspace.list.usage.atLimit")
              : remaining == null
                ? t("workspace.list.usage.unlimitedDescription")
                : t("workspace.list.usage.remaining", {
                    remaining: formatCount(remaining),
                  })}
          </div>
        </div>
        <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {t("workspace.list.usage.value", {
            used: formatCount(used),
            limit: limitLabel,
          })}
        </div>
      </div>
      {limit != null && (
        <div className="mt-3 h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`${barColor} h-2 rounded-full transition-all`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  )
}

const WorkspaceListPage = () => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const { currentOrganization, userMe, workspaces, setWorkspaces } = useStore(
    (state) => ({
      currentOrganization: state.currentOrganization,
      userMe: state.userMe,
      workspaces: state.workspaces,
      setWorkspaces: state.setWorkspaces,
    })
  )

  const [searchParams, setSearchParams] = useSearchParams()
  const [editWorkspace, setEditWorkspace] = useState<Workspace | undefined>()
  const [showWorkspaceDialog, setShowWorkspaceDialog] = useState(false)
  const [groupFilter, setGroupFilter] = useState(GroupFilterType.All)
  const [nameFilter, setNameFilter] = useState("")
  const [selectedMembersWorkspace, setSelectedMembersWorkspace] = useState<
    Workspace | undefined
  >()
  const [showMembersDialog, setShowMembersDialog] = useState(false)
  const [showAddMembersDialog, setShowAddMembersDialog] = useState(false)
  const [members, setMembers] = useState<Member[]>([])
  const selectedMembersWorkspaceId = selectedMembersWorkspace?.id || ""
  const pendingAddedMemberIdsRef = useRef<Set<number>>(new Set())
  const pendingRemovedMemberIdsRef = useRef<Set<number>>(new Set())

  // APIs
  const {
    status: workspacesStatus,
    error: workspacesError,
    data: workspacesResp,
    fetchFn: getWorkspaces,
  } = useGetWorkspacesApi(currentOrganization!.id)
  const { data: limitUsageResp, fetchFn: getLimitUsage } =
    useGetOrganizationLimitUsageApi(currentOrganization!.id)
  const {
    error: membersError,
    data: membersResp,
    fetchFn: getMembers,
  } = useGetWorkspaceUsersApi(
    currentOrganization!.id,
    selectedMembersWorkspaceId
  )
  const { fetchFn: addMembers } = useAddWorkspaceMembersApi(
    currentOrganization!.id,
    selectedMembersWorkspaceId
  )
  const loading = workspacesStatus === FetchStatus.Loading

  const refreshLimitUsage = useCallback(() => {
    getLimitUsage({ preserveData: true }).catch(() => {
      alertToast({
        title: t("workspace.list.usage.errors.loadFailedTitle"),
        description: t("workspace.list.usage.errors.loadFailedDescription"),
      })
    })
  }, [alertToast, getLimitUsage, t])

  const resetPendingMemberIds = useCallback(() => {
    pendingAddedMemberIdsRef.current = new Set()
    pendingRemovedMemberIdsRef.current = new Set()
  }, [])

  const syncWorkspaceMembers = useCallback(
    (workspaceId: string, nextMembers: User[]) => {
      setSelectedMembersWorkspace((prev) => {
        if (!prev || prev.id !== workspaceId) return prev
        return {
          ...prev,
          members: nextMembers,
        }
      })
      setWorkspaces((prev) =>
        prev.map((workspace) =>
          workspace.id === workspaceId
            ? {
                ...workspace,
                members: nextMembers,
              }
            : workspace
        )
      )
    },
    [setWorkspaces]
  )

  const mergeFetchedMembers = useCallback(
    (fetchedMembers: Member[], currentMembers: Member[]) => {
      const visibleFetchedMembers = fetchedMembers.filter(
        (member) => !pendingRemovedMemberIdsRef.current.has(member.id)
      )
      const visibleFetchedIds = new Set(
        visibleFetchedMembers.map((member) => member.id)
      )
      const optimisticAddedMembers = currentMembers.filter(
        (member) =>
          pendingAddedMemberIdsRef.current.has(member.id) &&
          !visibleFetchedIds.has(member.id)
      )

      return [...visibleFetchedMembers, ...optimisticAddedMembers]
    },
    []
  )

  // fetch on mount
  useEffect(() => {
    getWorkspaces()
    refreshLimitUsage()
  }, [getWorkspaces, refreshLimitUsage])

  const updateSearchParams = (page?: number) => {
    const params = new URLSearchParams(searchParams.toString())
    if (page) {
      params.set("page", page.toString())
    }
    setSearchParams(params)
  }

  const paginateWorkspaces = useCallback(
    (workspaces: Workspace[]) => {
      const page = parseInt(searchParams.get("page") || "1")
      const offset = (page - 1) * DEFAULT_PAGE_SIZE
      return workspaces.slice(offset, offset + DEFAULT_PAGE_SIZE)
    },
    [searchParams]
  )

  useEffect(() => {
    if (!workspacesResp) return
    if (workspacesResp?.data) {
      const newWorkspaces = workspacesResponseToWorkspaces(workspacesResp)
      setWorkspaces(newWorkspaces)
    }
  }, [setWorkspaces, workspacesResp])

  useEffect(() => {
    if (!workspacesError) return
    console.error("Failed to get workspaces", workspacesError)

    alertToast({
      title: t("workspace.list.errors.loadFailed"),
      description: getUserFacingApiErrorMessage(
        workspacesError,
        t,
        workspacesResp?.message || t("workspace.list.errors.loadFailed")
      ),
    })
  }, [workspacesError, workspacesResp, alertToast, t])

  useEffect(() => {
    if (!showMembersDialog || !selectedMembersWorkspaceId) return
    setMembers([])
    resetPendingMemberIds()
    getMembers()
  }, [
    getMembers,
    resetPendingMemberIds,
    selectedMembersWorkspaceId,
    showMembersDialog,
  ])

  useEffect(() => {
    if (!membersResp?.data || !selectedMembersWorkspaceId) return
    const fetchedMembers = workspaceUsersResponseToMembers(membersResp)
    const fetchedIds = new Set(fetchedMembers.map((member) => member.id))
    let mergedMembers: Member[] = []

    setMembers((prev) => {
      const nextMembers = mergeFetchedMembers(fetchedMembers, prev)
      mergedMembers = nextMembers
      return areMembersEqual(prev, nextMembers) ? prev : nextMembers
    })
    syncWorkspaceMembers(selectedMembersWorkspaceId, mergedMembers)

    const nextPendingAdded = new Set(pendingAddedMemberIdsRef.current)
    fetchedIds.forEach((id) => nextPendingAdded.delete(id))
    pendingAddedMemberIdsRef.current = nextPendingAdded

    const nextPendingRemoved = new Set<number>()
    pendingRemovedMemberIdsRef.current.forEach((id) => {
      if (fetchedIds.has(id)) nextPendingRemoved.add(id)
    })
    pendingRemovedMemberIdsRef.current = nextPendingRemoved
  }, [
    membersResp,
    mergeFetchedMembers,
    selectedMembersWorkspaceId,
    syncWorkspaceMembers,
  ])

  const currentPage = parseInt(searchParams.get("page") || "1")

  const filteredWorkspaces = useMemo(() => {
    if (!userMe) return workspaces

    let nextWorkspaces = [...workspaces]
      .sort((a, b) => compareAsc(b.createdAt, a.createdAt))
      .filter((workspace) =>
        workspace.name.toLowerCase().includes(nameFilter.toLowerCase())
      )
    if (groupFilter === GroupFilterType.Owned) {
      nextWorkspaces = nextWorkspaces.filter(
        (workspace) => workspace.owner.id === userMe.id
      )
    } else if (groupFilter === GroupFilterType.Joined) {
      nextWorkspaces = nextWorkspaces.filter((workspace) => workspace.isJoined)
    } else if (groupFilter === GroupFilterType.NoOwner) {
      nextWorkspaces = nextWorkspaces.filter(
        (workspace) => workspace.owner === NoOwner
      )
    }

    return nextWorkspaces
  }, [groupFilter, nameFilter, userMe, workspaces])

  const totalPages = Math.ceil(filteredWorkspaces.length / DEFAULT_PAGE_SIZE)
  const displayWorkspaces = useMemo(
    () => paginateWorkspaces(filteredWorkspaces),
    [filteredWorkspaces, paginateWorkspaces]
  )
  const workspaceUsage = useMemo(() => {
    if (!limitUsageResp?.data) return null
    return (
      transformOrganizationLimitUsage(limitUsageResp.data).items.find(
        (item) => item.key === "workspaces"
      ) ?? null
    )
  }, [limitUsageResp?.data])
  const newWorkspaceDisabledByUsage = Boolean(workspaceUsage?.atLimit)

  const handleNewButtonClick = () => {
    if (newWorkspaceDisabledByUsage) return
    setEditWorkspace(undefined)
    setShowWorkspaceDialog(true)
  }

  const handleEditWorkspace = (workspace: Workspace) => {
    setEditWorkspace(workspace)
    setShowWorkspaceDialog(true)
  }

  const refreshWorkspaces = () => {
    getWorkspaces()
    refreshLimitUsage()
  }

  const handleOpenMembers = (workspace: Workspace) => {
    setSelectedMembersWorkspace(workspace)
    setShowMembersDialog(true)
  }

  const handleMembersDialogChange = (open: boolean) => {
    setShowMembersDialog(open)
    if (!open) {
      setShowAddMembersDialog(false)
      resetPendingMemberIds()
    }
  }

  const handleMemberRemoved = (member: Member) => {
    pendingRemovedMemberIdsRef.current = new Set(
      pendingRemovedMemberIdsRef.current
    ).add(member.id)
    const nextPendingAdded = new Set(pendingAddedMemberIdsRef.current)
    nextPendingAdded.delete(member.id)
    pendingAddedMemberIdsRef.current = nextPendingAdded

    const nextMembers = members.filter((current) => current.id !== member.id)
    setMembers(nextMembers)
    if (selectedMembersWorkspaceId) {
      syncWorkspaceMembers(selectedMembersWorkspaceId, nextMembers)
    }
  }

  const handlePageChange = (page: number) => {
    updateSearchParams(page)
  }

  const handleFilterChange = (value: string) => {
    setNameFilter(value)
  }

  return (
    <>
      <div className="flex flex-col space-y-1.5 mt-4 mb-4 pl-2">
        <h3 className="text-2xl font-semibold leading-none tracking-tight">
          {t("workspace.list.title", { count: workspaces.length })}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t("workspace.list.description")}
        </p>
        <FilterBadges
          user={userMe}
          workspaces={workspaces}
          selected={groupFilter}
          onFilterChange={setGroupFilter}
        />
        <div className="flex flex-col gap-3 pt-6 pb-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="sm:max-w-md sm:flex-1">
            {workspaceUsage ? (
              <WorkspaceUsageStrip usage={workspaceUsage} />
            ) : null}
          </div>
          <Button
            onClick={handleNewButtonClick}
            disabled={newWorkspaceDisabledByUsage}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("workspace.new")}
          </Button>
        </div>
        <div className="flex justify-between pb-2">
          <SearchInput
            className="w-full sm:w-2/6"
            placeholder={t("workspace.list.filterPlaceholder")}
            onChange={handleFilterChange}
          />
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
          {Array.from({ length: 8 }).map((_, idx) => (
            <Skeleton key={idx} className="h-40 w-full" />
          ))}
        </div>
      ) : displayWorkspaces.length === 0 ? (
        <div className="flex justify-center text-gray-500 text-center my-12">
          <EmptyDataPanel />
        </div>
      ) : (
        <PageProvider<Workspace>
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10"
          data={displayWorkspaces}
          totalPages={totalPages}
          defaultPage={currentPage}
          onPageChange={handlePageChange}
          renderItem={(item, idx) => (
            <WorkspaceCard
              key={idx}
              workspace={item}
              onEdit={handleEditWorkspace}
              onDelete={refreshWorkspaces}
              onMembers={handleOpenMembers}
            />
          )}
        />
      )}

      {/* Dialogs */}
      <WorkspaceDialog
        open={showWorkspaceDialog}
        editWorkspace={editWorkspace}
        onOpenChange={(open) => setShowWorkspaceDialog(open)}
        onConfirm={refreshWorkspaces}
      />
      <WorkspaceMembersDialog
        organization={currentOrganization!}
        workspace={selectedMembersWorkspace}
        members={members}
        membersLoaded={Boolean(membersResp)}
        membersError={membersError}
        open={showMembersDialog}
        addDialogOpen={showAddMembersDialog}
        onOpenChange={handleMembersDialogChange}
        onAddDialogOpenChange={setShowAddMembersDialog}
        onLeaveMember={handleMemberRemoved}
        onConfirmAddMembers={(newUsers) => {
          const appendedMembers = newUsers.map(
            (user) =>
              ({
                ...user,
                role: WorkspaceRole.Member,
                permissions: new Set<string>(),
              }) satisfies Member
          )

          const nextPendingAdded = new Set(pendingAddedMemberIdsRef.current)
          newUsers.forEach((user) => nextPendingAdded.add(user.id))
          pendingAddedMemberIdsRef.current = nextPendingAdded

          const nextPendingRemoved = new Set(pendingRemovedMemberIdsRef.current)
          newUsers.forEach((user) => nextPendingRemoved.delete(user.id))
          pendingRemovedMemberIdsRef.current = nextPendingRemoved

          const existingIds = new Set(members.map((member) => member.id))
          const nextMembers = [
            ...members,
            ...appendedMembers.filter((member) => !existingIds.has(member.id)),
          ]
          setMembers(nextMembers)
          syncWorkspaceMembers(selectedMembersWorkspaceId, nextMembers)

          addMembers({
            jsonBody: { user_ids: newUsers.map((u) => Number(u.id)) },
            callbackFn: (resp) => {
              if (resp?.data) {
                const updatedWorkspace = workspaceResponseToWorkspace(resp.data)
                setWorkspaces((prev) =>
                  prev.map((workspace) => {
                    if (workspace.id !== updatedWorkspace.id) return workspace

                    return {
                      ...updatedWorkspace,
                      members: workspace.members,
                    }
                  })
                )
              }
              setShowAddMembersDialog(false)
            },
          })
        }}
      />
    </>
  )
}

const FilterBadges = ({
  user,
  workspaces,
  selected,
  onFilterChange,
}: {
  user: User | null
  workspaces: Workspace[]
  selected: GroupFilterType
  onFilterChange: (filter: GroupFilterType) => void
}) => {
  const { t } = useTranslation()
  const ownedCount = workspaces.filter(
    (workspace) => workspace.owner.id === user?.id
  ).length
  const joinedCount = workspaces.filter(
    (workspace) => workspace.isJoined
  ).length
  const noOwnerCount = workspaces.filter(
    (workspace) => workspace.owner === NoOwner
  ).length
  return (
    <div className="flex-auto space-x-2 space-y-2">
      <Badge
        className="cursor-pointer"
        onClick={() => onFilterChange(GroupFilterType.All)}
        variant={selected === GroupFilterType.All ? "default" : "secondary"}
      >
        {t("workspace.list.filter.all", { count: workspaces.length })}
      </Badge>
      <Badge
        className="cursor-pointer"
        onClick={() => onFilterChange(GroupFilterType.Owned)}
        variant={selected === GroupFilterType.Owned ? "default" : "secondary"}
      >
        {t("workspace.list.filter.owned", { count: ownedCount })}
      </Badge>
      <Badge
        className="cursor-pointer"
        onClick={() => onFilterChange(GroupFilterType.Joined)}
        variant={selected === GroupFilterType.Joined ? "default" : "secondary"}
      >
        {t("workspace.list.filter.joined", { count: joinedCount })}
      </Badge>
      <Badge
        className="cursor-pointer"
        onClick={() => onFilterChange(GroupFilterType.NoOwner)}
        variant={selected === GroupFilterType.NoOwner ? "default" : "secondary"}
      >
        {t("workspace.list.filter.noOwner", { count: noOwnerCount })}
      </Badge>
    </div>
  )
}

export default WorkspaceListPage
